/**
 * Queue draining helpers for offline-first sync (#1983).
 *
 * The sync engine asks `selectDrainableRows()` for the work-list it
 * should attempt this tick: only `pending` rows whose parent visits
 * (if any) have a server-assigned calllogid. Result is sorted by
 * priority asc, then createdAt asc so check-ins drain first and the
 * order within a priority class is FIFO.
 */

import { STORES, getAll, put } from '../utils/indexedDB';
import { resolveParentCallLogId } from './httpHelpers';
import type { QueueRow, LocalId } from './types';

/**
 * True if the op has no parent, or if its parent visit's CallLog has
 * been minted server-side (i.e. visit_task_sessions.callLogId is a
 * number). False when the parent row is missing or its callLogId is
 * still null.
 */
export async function isParentSynced(
  parentLocalId: LocalId | null | undefined,
): Promise<boolean> {
  if (!parentLocalId) return true;
  const cid = await resolveParentCallLogId(parentLocalId);
  return cid !== undefined;
}

/**
 * Subset of visit_sync_queue rows that are eligible to fire on this
 * drain tick. Rows in 'syncing' / 'failed' are excluded — only
 * 'pending' rows whose dependency is satisfied (or absent) are
 * returned, sorted by priority asc then createdAt asc.
 */
export async function selectDrainableRows(): Promise<QueueRow[]> {
  const all = (await getAll(STORES.VISIT_SYNC_QUEUE)) as QueueRow[];
  const pending = all.filter((r) => r.status === 'pending');

  const eligible: QueueRow[] = [];
  for (const row of pending) {
    if (await isParentSynced(row.dependsOn ?? null)) {
      eligible.push(row);
    }
  }

  eligible.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.createdAt.localeCompare(b.createdAt);
  });

  return eligible;
}

/**
 * Move permanently-stuck dependents to `failed` so the queue self-heals
 * (diag #43). A `pending` row whose `dependsOn` parent has no server
 * calllogid AND has no LIVE parent op left in the queue (a pending/syncing
 * row whose relatedTaskUUID is that parent) can never drain — nothing will
 * ever mint the parent's calllogid. Left as `pending` it sticks the
 * "N actions to sync" banner forever and makes "Sync Now" a no-op. Failing
 * it surfaces it on the Sync Issues page (Retry / Discard) instead.
 *
 * Returns the number of rows swept to `failed`. Conservative by design: a
 * dependent is spared whenever its parent visit has synced OR a live parent
 * op still exists to resolve it, so a normal check-in -> survey ordering is
 * never falsely failed.
 */
export async function sweepOrphanedRows(): Promise<number> {
  const all = (await getAll(STORES.VISIT_SYNC_QUEUE)) as QueueRow[];

  // Parents that something live will still resolve: a pending/syncing op
  // whose relatedTaskUUID IS the parent localId.
  const liveParents = new Set<string>(
    all
      .filter((r) => (r.status === 'pending' || r.status === 'syncing') && r.relatedTaskUUID)
      .map((r) => r.relatedTaskUUID as string),
  );

  let swept = 0;
  for (const row of all) {
    if (row.status !== 'pending') continue;
    const parent = row.dependsOn ?? null;
    if (!parent) continue; // no dependency — drains on its own
    if (await isParentSynced(parent)) continue; // parent already has a calllogid
    if (liveParents.has(parent)) continue; // a live parent op will still resolve it
    // Orphaned: the parent will never produce a calllogid.
    row.status = 'failed';
    row.failedReason = 'Parent visit never synced — discard this item or fix the check-in.';
    row.lastAttempt = new Date().toISOString();
    await put(STORES.VISIT_SYNC_QUEUE, row);
    swept += 1;
  }

  if (swept > 0 && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('rdm:sync-failed'));
  }
  return swept;
}
